// Reinforcement calculation — implements the formal spec from docs/gameplay.md.
// Pure: no DB calls, no side effects.

import type { GameEvent, GameState, PlayerId, TerritoryId } from './types.js'
import {
  armyHeadroom,
  canPlaceOn,
  placeableArmies,
  reinforcementCount as sharedReinforcementCount,
} from '@warcarp/api-types'

/**
 * Calculate how many new armies a player receives at the start of their turn.
 *
 * Formula: count = max(3, floor(territoriesOwned / 3))
 *                + sum(region.bonus for each region the player fully owns)
 *                + state.bonusArmies[playerId]  (card turn-ins + eliminate-player)
 *
 * `3` is the classic Risk minimum on the territory base. Eliminated players
 * (zero territories owned) draw 0 — the floor doesn't apply, and the pending
 * bonus pool is dropped because there's nowhere to spend it.
 *
 * Delegates to the shared implementation in @warcarp/api-types so the engine
 * and mobile client always agree on the formula.
 */
export function reinforcementCount(state: GameState, playerId: PlayerId): number {
  const { map } = state
  const owned = map.territories.filter((t) => t.ownerId === playerId)
  const bonusArmies = state.bonusArmies?.[playerId] ?? 0
  return sharedReinforcementCount(owned, map.regions, map.territories, bonusArmies, playerId)
}

/**
 * Interrupt budget for the post-elim-placement phase.
 *
 * Returns only the attacker's current bonusArmies — NOT the base or region
 * bonuses that reinforcementCount includes. Used exclusively during the
 * 'post-elim-placement' phase: the attacker may spend their just-credited
 * elimination bonus (plus any bonuses from card turn-ins on inherited cards)
 * but NOT their standard per-territory/region allotment, which is deferred
 * to their next normal placement phase.
 */
export function interruptReinforcementCount(state: GameState, playerId: PlayerId): number {
  return state.bonusArmies?.[playerId] ?? 0
}

export interface ReinforcePlacement {
  territoryId: TerritoryId
  armies: number
}

/**
 * Validate a set of placements against a budget, for both the placement phase
 * (budget = `reinforcementCount`) and the post-elimination interrupt
 * (budget = `interruptReinforcementCount`). Routes delegate here.
 *
 * - Every territory must exist and be placeable by `playerId`: their own, or a
 *   teammate's in a team game (`canPlaceOn`; plan §5).
 * - No territory may go over its troop cap.
 * - The total must equal what can actually be placed: the full budget, or
 *   less when troop caps across every placeable territory (own plus allies)
 *   leave less room, so a capped map can't soft-lock the phase.
 *
 * In free-for-all the checks and messages are exactly the old inline route
 * checks.
 */
export function canReinforce(
  state: GameState,
  playerId: PlayerId,
  placements: readonly ReinforcePlacement[],
  budget: number,
): { allowed: true } | { allowed: false; reason: string } {
  for (const p of placements) {
    const territory = state.map.territories.find((t) => t.id === p.territoryId)
    if (!territory) {
      return { allowed: false, reason: `Territory '${p.territoryId}' does not exist` }
    }
    if (!canPlaceOn(state, playerId, territory)) {
      return {
        allowed: false,
        reason: state.teams
          ? `Territory '${p.territoryId}' is not yours or a teammate's`
          : `Territory '${p.territoryId}' is not owned by you`,
      }
    }
    // Troop cap. Absent maxArmies = unlimited, the default everywhere.
    const headroom = armyHeadroom(territory)
    if (p.armies > headroom) {
      return {
        allowed: false,
        reason: `'${territory.name}' holds at most ${territory.maxArmies} armies (room for ${headroom} more)`,
      }
    }
  }

  const totalPlaced = placements.reduce((s, p) => s + p.armies, 0)
  const placeable = placeableArmies(
    budget,
    state.map.territories.filter((t) => canPlaceOn(state, playerId, t)),
  )
  if (totalPlaced !== placeable) {
    return {
      allowed: false,
      reason:
        placeable < budget
          ? `Troop caps leave room for only ${placeable} of your ${budget} armies (you placed ${totalPlaced})`
          : `You must place exactly ${budget} armies (you placed ${totalPlaced})`,
    }
  }
  return { allowed: true }
}

/**
 * Apply a set of reinforcement placements for `playerId`.
 *
 * Caller is responsible for:
 *   - verifying state.phase === 'placement'
 *   - verifying the placements with `canReinforce` (ownership or a teammate's
 *     territory, troop caps, and the budget)
 *   - deciding when to call `advancePhase` afterward (route or bot driver)
 *
 * Pure: never mutates `state`; returns a new object with armies added.
 */
export function applyReinforce(
  state: GameState,
  playerId: PlayerId,
  placements: ReinforcePlacement[],
): GameState {
  const newTerritories = state.map.territories.map((t) => {
    const placement = placements.find((p) => p.territoryId === t.id)
    if (placement) {
      return { ...t, armies: t.armies + placement.armies }
    }
    return t
  })

  const placementEvent: GameEvent = {
    kind: 'placement',
    player: playerId,
    placements: placements.map((p) => ({ territoryId: p.territoryId, armies: p.armies })),
    ts: Date.now(),
  }

  return {
    ...state,
    map: { ...state.map, territories: newTerritories },
    events: [...(state.events ?? []), placementEvent],
  }
}

/**
 * Clear the placement-phase counters that don't carry over to attack:
 *   - `bonusArmies[playerId]` → 0 (already spent on the just-placed armies,
 *     or forfeited if the player skips placement entirely)
 *   - `mustTurnInCards` → false (the 5+ cap only applies during placement)
 *
 * Called by every route that transitions a player OUT of the placement
 * phase: /turn/reinforce (placement → attack), /turn/advance (skip-out
 * of placement), and /turn/post-elim-place (interrupt → attack). Keeps
 * the placement-exit semantics in one place so a future fourth path
 * (bot driver, replay) can't forget the zeroing.
 *
 * Pure; phase is NOT changed here — the caller controls phase
 * progression. This function only touches the cleanup fields.
 */
export function applyPlacementExit(state: GameState, playerId: PlayerId): GameState {
  return {
    ...state,
    bonusArmies: {
      ...(state.bonusArmies ?? {}),
      [playerId]: 0,
    },
    mustTurnInCards: false,
  }
}
